import { useEffect, useState } from "react";
import type { Category } from "../api/types";
import { useDeleteCategory, useUpdateCategory } from "../hooks/useCategories";
import ConfirmDialog from "./ConfirmDialog";
import Modal from "./Modal";

interface ManageCategoriesDialogProps {
  open: boolean;
  categories: Category[];
  onClose: () => void;
}

export default function ManageCategoriesDialog({ open, categories, onClose }: ManageCategoriesDialogProps) {
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const deleteCategory = useDeleteCategory();

  return (
    <>
      <Modal open={open} onClose={onClose} title="Manage categories">
        {categories.length === 0 ? (
          <p className="text-sm text-muted">No categories yet.</p>
        ) : (
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {categories.map((category) => (
              <CategoryRow
                key={category.id}
                category={category}
                onDelete={() => setDeleteTarget(category)}
              />
            ))}
          </div>
        )}
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-text hover:bg-bg"
          >
            Done
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete category"
        description={<>Delete “{deleteTarget?.name}”? Books in it are kept and become uncategorized.</>}
        confirmLabel="Delete"
        danger
        pending={deleteCategory.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteCategory.mutate(deleteTarget.id, {
            onSuccess: () => setDeleteTarget(null),
          });
        }}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
}

function CategoryRow({ category, onDelete }: { category: Category; onDelete: () => void }) {
  const [name, setName] = useState(category.name);
  const [chatPrompt, setChatPrompt] = useState(category.chat_prompt);
  const updateCategory = useUpdateCategory();

  // Re-sync local edit state whenever the server copy changes (e.g. after this row's own save).
  useEffect(() => {
    setName(category.name);
    setChatPrompt(category.chat_prompt);
  }, [category.name, category.chat_prompt]);

  const trimmedName = name.trim();
  const dirty = trimmedName !== category.name || chatPrompt !== category.chat_prompt;

  function handleSave() {
    if (!trimmedName || !dirty) return;
    updateCategory.mutate({ id: category.id, payload: { name: trimmedName, chat_prompt: chatPrompt } });
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-3">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label={`Rename category ${category.name}`}
          className="w-full rounded-md border border-border bg-bg px-2.5 py-1.5 text-sm font-medium text-text"
        />
        <span className="whitespace-nowrap text-xs text-muted">
          {category.book_count} {category.book_count === 1 ? "book" : "books"}
        </span>
      </div>

      <div className="mt-2">
        <textarea
          value={chatPrompt}
          onChange={(e) => setChatPrompt(e.target.value)}
          rows={2}
          aria-label={`Chat prompt for ${category.name}`}
          className="w-full resize-none rounded-md border border-border bg-bg px-2.5 py-1.5 text-sm text-text"
        />
        <p className="mt-1 text-xs text-muted">
          Optional standing instruction for chats in this category, e.g. “Answer like a fitness coach.”
        </p>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <button type="button" onClick={onDelete} className="text-xs font-medium text-danger hover:underline">
          Delete
        </button>
        <div className="flex items-center gap-2">
          {updateCategory.isError ? (
            <span className="text-xs text-danger">{updateCategory.error.message}</span>
          ) : updateCategory.isSuccess && !dirty ? (
            <span className="text-xs text-accent-strong">Saved.</span>
          ) : null}
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || !trimmedName || updateCategory.isPending}
            className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-strong disabled:opacity-50"
          >
            {updateCategory.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
